/** Thin typed client. Every call carries the session cookie. */

const BASE = import.meta.env.DEV ? '/api' : (import.meta.env.VITE_API_URL ?? '');

export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        ...(init.body ? { 'content-type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  } catch {
    throw new ApiClientError(0, 'offline', 'אין חיבור לשרת כרגע.');
  }

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => null) as { data?: T; error?: { code: string; message: string; details?: unknown } } | null;

  if (!response.ok) {
    throw new ApiClientError(
      response.status,
      payload?.error?.code ?? 'unknown',
      payload?.error?.message ?? 'משהו השתבש. נסה שוב.',
      payload?.error?.details,
    );
  }
  return (payload?.data ?? payload) as T;
}

const body = (data: unknown) => ({ body: JSON.stringify(data) });

export const api = {
  auth: {
    status: () => request<AuthStatus>('/auth/status'),
    devLogin: (email: string, name?: string) => request<{ user: User }>('/auth/dev-login', { method: 'POST', ...body({ email, name }) }),
    signup: (input: { email: string; password: string; name?: string; family?: { mode: 'create'; name: string } | { mode: 'join'; code: string } }) =>
      request<{ user: User; family: { name: string; role: 'family_admin' | 'member'; joinCode?: string } | null }>('/auth/signup', { method: 'POST', ...body(input) }),
    login: (email: string, password: string) => request<{ user: User }>('/auth/login', { method: 'POST', ...body({ email, password }) }),
    logout: () => request<{ loggedOut: boolean }>('/auth/logout', { method: 'POST' }),
    googleUrl: (redirectTo = '/') => `${BASE}/auth/google?redirect_to=${encodeURIComponent(redirectTo)}`,
  },
  family: {
    status: () => request<FamilyStatus>('/family/status'),
    create: (name: string) => request<{ name: string; role: 'family_admin'; joinCode: string }>('/family/create', { method: 'POST', ...body({ name }) }),
    join: (code: string) => request<{ name: string; role: 'member' }>('/family/join', { method: 'POST', ...body({ code }) }),
    leave: () => request<{ left: boolean }>('/family/leave', { method: 'POST' }),
    members: () => request<FamilyMember[]>('/family/members'),
    memberDashboard: (id: string) => request<{ member: User; dashboard: Dashboard }>(`/family/members/${id}/dashboard`),
  },
  couple: {
    status: () => request<CoupleStatus>('/couple/status'),
    invite: () => request<{ joinCode: string }>('/couple/invite', { method: 'POST' }),
    join: (code: string) => request<{ status: 'together' }>('/couple/join', { method: 'POST', ...body({ code }) }),
    proposeMarriage: () => request<{ status: 'marriage_pending' }>('/couple/propose-marriage', { method: 'POST' }),
    confirmMarriage: () => request<{ status: 'married' }>('/couple/confirm-marriage', { method: 'POST' }),
    leave: () => request<{ left: boolean }>('/couple/leave', { method: 'POST' }),
    dashboard: () => request<CoupleDashboard>('/couple/dashboard'),
  },
  bank: {
    status: () => request<BankStatus>('/bank/status'),
    connectUrl: () => `${BASE}/bank/connect`,
    sync: () => request<{ accountsLinked: number; transactionsAdded: number; errored: string[] }>('/bank/sync', { method: 'POST' }),
    disconnect: (id: string) => request<{ disconnected: boolean }>(`/bank/connections/${id}`, { method: 'DELETE' }),
  },
  me: {
    get: () => request<MeResponse>('/me'),
    update: (patch: Partial<{ name: string; currency: string; timezone: string; onboarded: boolean }>) =>
      request<{ user: User }>('/me', { method: 'PATCH', ...body(patch) }),
    preferences: (patch: Partial<Preferences>) =>
      request<{ preferences: Preferences }>('/me/preferences', { method: 'PATCH', ...body(patch) }),
  },
  dashboard: {
    get: () => request<Dashboard>('/dashboard'),
  },
  accounts: {
    list: () => request<Account[]>('/accounts'),
    create: (input: NewAccount) => request<Account>('/accounts', { method: 'POST', ...body(input) }),
    update: (id: string, patch: Partial<NewAccount & { archived: boolean }>) =>
      request<Account>(`/accounts/${id}`, { method: 'PATCH', ...body(patch) }),
    remove: (id: string) => request<{ archived: boolean }>(`/accounts/${id}`, { method: 'DELETE' }),
  },
  categories: {
    list: () => request<Category[]>('/categories'),
  },
  transactions: {
    list: (params: Record<string, string | number | undefined> = {}) => {
      const query = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== '') query.set(key, String(value));
      }
      return request<Transaction[]>(`/transactions?${query.toString()}`);
    },
    create: (input: NewTransaction) => request<Transaction>('/transactions', { method: 'POST', ...body(input) }),
    update: (id: string, patch: Partial<NewTransaction> & { version?: number }) =>
      request<Transaction>(`/transactions/${id}`, { method: 'PATCH', ...body(patch) }),
    remove: (id: string) => request<{ deleted: boolean }>(`/transactions/${id}`, { method: 'DELETE' }),
    restore: (id: string) => request<{ restored: boolean }>(`/transactions/${id}/restore`, { method: 'POST' }),
    setSplits: (id: string, splits: { categoryId: string | null; amount: number; note?: string }[]) =>
      request<Transaction>(`/transactions/${id}/splits`, { method: 'PUT', ...body({ splits }) }),
  },
  plan: {
    overview: () => request<PlanOverview>('/plan/overview'),
    setBudget: (input: { categoryId?: string | null; amount: number }) =>
      request<Budget>('/plan/budgets', { method: 'POST', ...body(input) }),
    removeBudget: (id: string) => request<{ deleted: boolean }>(`/plan/budgets/${id}`, { method: 'DELETE' }),
    goals: () => request<GoalWithProjection[]>('/plan/goals'),
    createGoal: (input: NewGoal) => request<Goal>('/plan/goals', { method: 'POST', ...body(input) }),
    updateGoal: (id: string, patch: Partial<NewGoal> & { status?: string }) =>
      request<Goal>(`/plan/goals/${id}`, { method: 'PATCH', ...body(patch) }),
    removeGoal: (id: string) => request<{ deleted: boolean }>(`/plan/goals/${id}`, { method: 'DELETE' }),
    goalWhatIf: (id: string, monthlyContribution: number) =>
      request<GoalWhatIf>(`/plan/goals/${id}/what-if`, { method: 'POST', ...body({ monthlyContribution }) }),
    recurring: () => request<Recurring[]>('/plan/recurring'),
    detectRecurring: () =>
      request<{ candidates: RecurringCandidate[]; priceChanges: PriceChange[] }>('/plan/recurring/detect', { method: 'POST' }),
    confirmRecurring: (input: { merchant: string; expectedAmount: number; interval: string; nextExpectedDate?: string | null; categoryId?: string | null; confidence?: number }) =>
      request<Recurring>('/plan/recurring', { method: 'POST', ...body(input) }),
    updateRecurring: (id: string, patch: Record<string, unknown>) =>
      request<Recurring>(`/plan/recurring/${id}`, { method: 'PATCH', ...body(patch) }),
    emergencyFund: () => request<EmergencyFund>('/plan/emergency-fund'),
  },
  insights: {
    list: () => request<Insight[]>('/insights'),
    trends: (period: string) => request<Trends>(`/insights/trends?period=${period}`),
    categories: (period: string) => request<CategoryAnalysis>(`/insights/categories?period=${period}`),
    comparison: () => request<ComparisonResponse>('/insights/comparison'),
    forecast: () => request<Forecast>('/insights/forecast'),
  },
  scenarios: {
    run: (input: Record<string, unknown>) => request<ScenarioResult>('/scenarios', { method: 'POST', ...body(input) }),
  },
  ai: {
    status: () => request<{ available: boolean; provider: string | null; model: string | null }>('/ai/status'),
    ask: (question: string) => request<AskResult>('/ai/ask', { method: 'POST', ...body({ question }) }),
    categorize: (input: { merchant?: string | null; description?: string | null; amount?: number }) =>
      request<{ categoryId: string | null; confidence: number; reason: string; source?: string }>(
        '/ai/categorize', { method: 'POST', ...body(input) },
      ),
  },
  receipts: {
    analyze: (imageBase64: string, mimeType: string) =>
      request<ReceiptAnalysis>('/receipts/analyze', { method: 'POST', ...body({ imageBase64, mimeType }) }),
    confirm: (id: string, input: { accountId: string; merchant: string; total: number; occurredAt: number; categoryId?: string | null }) =>
      request<{ transactionId: string }>(`/receipts/${id}/confirm`, { method: 'POST', ...body(input) }),
  },
  google: {
    status: () => request<GoogleStatus>('/google/status'),
    connectUrl: (purpose: 'connect_drive' | 'connect_sheets' | 'connect_gmail_receipts') =>
      `${BASE}/auth/google?purpose=${purpose}&redirect_to=${encodeURIComponent('/profile')}`,
    backup: () => request<{ fileId: string; filename: string; at: number }>('/google/drive/backup', { method: 'POST' }),
    exportSheets: () => request<{ url: string; title: string }>('/google/sheets/export', { method: 'POST' }),
    disconnect: () => request<{ disconnected: boolean; message: string }>('/google/disconnect', { method: 'POST' }),
    /** Read-only Gmail scan for candidate receipts — never writes anything by itself. */
    gmailScan: (sinceDays?: number) =>
      request<GmailScanResult>('/google/gmail/scan', { method: 'POST', ...body({ sinceDays }) }),
  },
  imports: {
    preview: (input: { accountId: string; source: 'csv' | 'xlsx' | 'sheets' | 'api' | 'manual'; rows: Record<string, unknown>[] }) =>
      request<ImportPreview>('/imports/preview', { method: 'POST', ...body(input) }),
    commit: (input: { batchId: string; accountId: string; rows: ImportCommitRow[] }) =>
      request<{ imported: number; skipped: number }>('/imports/commit', { method: 'POST', ...body(input) }),
  },
  privacy: {
    exportUrl: () => `${BASE}/privacy/export`,
    deleteHistory: () =>
      request<{ deleted: boolean }>('/privacy/delete-financial-history', { method: 'POST', ...body({ confirm: 'DELETE' }) }),
    deleteAccount: (email: string) =>
      request<{ deleted: boolean }>('/privacy/delete-account', { method: 'POST', ...body({ confirm: 'DELETE', email }) }),
    audit: () => request<{ id: string; event: string; at: number }[]>('/privacy/audit'),
  },
};

export const rawRequest = request;

// ------------------------------------------------------------------ types

export interface User {
  id: string; email: string; name: string | null; avatarUrl: string | null;
  currency: string; locale: string; timezone: string; onboarded: boolean;
  // Disclosed on every user's own account — see FamilyStatus for the full picture. §DEMO
  familyId: string | null; familyRole: 'member' | 'family_admin';
  // Same disclosure principle for a two-person relationship — see CoupleStatus. §DEMO
  coupleId: string | null;
}

export type FamilyStatus =
  | { inFamily: false }
  | { inFamily: true; name: string; role: 'member' | 'family_admin'; memberCount: number; joinCode: string | null };

export interface FamilyMember extends User {
  summary: { safeToSpend: number; netWorth: number; monthExpenses: number } | null;
}

/**
 * Couples: mutual, disclosed, view-only — never a data merge. `married`
 * additionally unlocks a combined view (CoupleDashboard.merged), computed on
 * the fly from both partners' own numbers, never stored. §DEMO
 */
export type CoupleStatus =
  | { inCouple: false }
  | { inCouple: true; status: 'pending'; joinCode: string | null }
  | { inCouple: true; status: 'together' | 'marriage_pending' | 'married'; partnerName: string | null; proposedByMe: boolean; marriedAt: number | null };

export interface CoupleDashboard {
  status: 'together' | 'marriage_pending' | 'married';
  self: { user: User; dashboard: Dashboard };
  partner: { user: User; dashboard: Dashboard };
  merged: { netWorth: number; income: number; expenses: number; safeToSpend: number } | null;
}

export interface BankStatus {
  configured: boolean;
  providerName: string | null;
  connections: { id: string; label: string | null; status: 'active' | 'error' | 'revoked'; lastSyncedAt: number | null; lastError: string | null }[];
  /** Historical Bootstrap Engine — how much of the user's financial life the system can currently see. */
  coverage: {
    accounts: { id: string; name: string; coverageDays: number; score: number }[];
    overallScore: number;
  };
}

export interface AuthStatus {
  authenticated: boolean;
  user: User | null;
  providers: { google: boolean; devLogin: boolean };
}

export interface Preferences {
  monthlyBudget: number | null;
  safetyBuffer: number;
  reservedSavingsMonthly: number;
  emergencyFundMonths: number;
  paydayDayOfMonth: number | null;
  theme: 'system' | 'light' | 'dark';
  hideAmounts: boolean;
  aiEnabled: boolean;
}

export interface MeResponse {
  user: User;
  preferences: Preferences;
  connections: { google: { connected: boolean; email?: string | null; scopes?: string[] } };
}

export interface Account {
  id: string; name: string; type: string; currency: string; balance: number;
  institution: string | null; color: string | null; icon: string | null;
  isLiquid: boolean; isSpendable: boolean; includeInNetWorth: boolean;
  archived: boolean; sortOrder: number;
}

export interface NewAccount {
  name: string; type: string; currency?: string; balance?: number;
  institution?: string | null; isSpendable?: boolean; isLiquid?: boolean;
}

export interface Category {
  id: string; parentId: string | null; slug: string; name: string; nameHe: string | null;
  icon: string | null; color: string | null; kind: string; essential: boolean; system: boolean;
}

export interface TransactionSplit { id: string; categoryId: string | null; amount: number; note: string | null }

export interface Transaction {
  id: string; accountId: string; toAccountId: string | null; type: string;
  amount: number; currency: string; merchant: string | null; description: string | null;
  notes: string | null; tags: string[]; categoryId: string | null;
  recurringId: string | null; receiptId: string | null; occurredAt: number;
  pending: boolean; excludedFromAnalytics: boolean; source: string; version: number;
  deletedAt: number | null; splits: TransactionSplit[];
}

export interface NewTransaction {
  type: string; accountId: string; amount: number; toAccountId?: string | null;
  merchant?: string | null; description?: string | null; notes?: string | null;
  categoryId?: string | null; tags?: string[]; occurredAt?: number;
  excludedFromAnalytics?: boolean;
}

export interface UpcomingItem {
  id: string; sourceId: string; source: string; name: string;
  amount: number; dueDate: string; categoryId: string | null; confidence: number;
}

export interface Dashboard {
  today: string;
  currency: string;
  position: {
    netWorth: number; assets: number; liabilities: number;
    liquid: number; spendable: number; illiquid: number;
    byType: Record<string, number>;
  };
  safeToSpend: {
    amount: number; perDay: number; daysUntilNextIncome: number; nextIncomeDate: string;
    confidence: 'high' | 'medium' | 'low';
    breakdown: {
      availableLiquidCash: number; upcomingObligations: number;
      reservedSavings: number; requiredDebtPayments: number; safetyBuffer: number;
    };
    assumptions: { key: string; data?: Record<string, unknown>; text: string }[];
  };
  month: {
    period: { start: string; end: string };
    income: number; expenses: number; net: number; saved: number;
    savingsRate: number | null;
    pace: { spentSoFar: number; scheduledLater: number; daysElapsed: number; daysTotal: number; projectedTotal: number; budget: number | null; projectedOverBy: number | null };
    budget: null | {
      limit: number; spent: number; spentToDate: number; remaining: number; usage: number | null;
      projectedSpend: number; projectedBreachDate: string | null; status: string;
    };
  };
  upcoming: {
    next7Days: { total: number; items: UpcomingItem[] };
    next30Days: { total: number; count: number };
  };
  insight: null | { key: string; severity: 'info' | 'positive' | 'warning' | 'critical'; score: number; data: Record<string, unknown>; fallback: string };
  insightCount: number;
  goals: { id: string; name: string; target: number; current: number; progress: number; deadline: string | null; requiredMonthly: number | null; onTrack: boolean | null }[];
  emergencyFund: { coverageMonths: number | null; targetMonths: number; essentialMonthly: number; shortfall: number };
  charts: { balance: SeriesPoint[]; income: SeriesPoint[]; expenses: SeriesPoint[] };
  comparison: { income: Comparison; expenses: Comparison; net: Comparison };
  categories: {
    total: number;
    rows: { categoryId: string | null; amount: number; share: number }[];
    other: { amount: number; share: number } | null;
  };
  recent: { id: string; merchant: string | null; categoryId: string | null; amount: number; type: string; occurredAt: string }[];
  empty: boolean;
}

export interface SeriesPoint { date: string; value: number }

export interface Comparison { current: number; previous: number; delta: number; ratio: number | null }

// ------------------------------------------------------- plan & analysis

export interface Assumption { key: string; data?: Record<string, unknown>; text: string }

export interface Budget {
  id: string; categoryId: string | null; amount: number; currency: string;
  period: string; startDate: string; rollover: boolean; active: boolean;
}

export interface BudgetUsage {
  budgetId: string; categoryId: string | null; limit: number; spent: number; spentToDate: number;
  remaining: number; usage: number | null; projectedSpend: number; projectedOverBy: number;
  projectedBreachDate: string | null; daysElapsed: number; daysTotal: number;
  status: 'on_track' | 'watch' | 'over' | 'projected_over';
}

export interface Recurring {
  id: string; merchant: string; categoryId: string | null; accountId: string | null;
  expectedAmount: number; interval: string; intervalCount: number;
  nextExpectedDate: string | null; confidence: number; status: string; active: boolean;
}

export interface RecurringCandidate {
  merchant: string; merchantNorm: string; categoryId: string | null; accountId: string | null;
  interval: string; intervalCount: number; expectedAmount: number; lastAmount: number;
  lastSeen: string; nextExpectedDate: string; occurrences: number; confidence: number; signals: string[];
}

export interface PriceChange {
  merchant: string; previousAmount: number; currentAmount: number;
  deltaAmount: number; deltaRatio: number; since: string;
}

export interface Goal {
  id: string; name: string; icon: string | null; targetAmount: number; currentAmount: number;
  deadline: string | null; priority: string; status: string;
  sourceAccountId: string | null; monthlyContribution: number;
}

export interface GoalProjection {
  goalId: string; remaining: number; progress: number; monthlyContribution: number;
  monthsToTarget: number | null; projectedDate: string | null; requiredMonthly: number | null;
  monthsUntilDeadline: number | null; onTrack: boolean | null; deadlineDeltaMonths: number | null;
  confidence: string; assumptions: Assumption[];
}

export interface GoalWithProjection extends Goal { projection: GoalProjection }
export interface GoalWhatIf { base: GoalProjection; adjusted: GoalProjection; monthsSaved: number | null }

export interface NewGoal {
  name: string; targetAmount: number; currentAmount?: number; deadline?: string | null;
  priority?: 'must' | 'important' | 'nice'; monthlyContribution?: number;
}

export interface PlanOverview {
  today: string;
  month: { start: string; end: string };
  global: BudgetUsage | null;
  monthlyBudgetPreference: number | null;
  categories: BudgetUsage[];
  recurring: {
    id: string; merchant: string; amount: number; interval: string;
    nextExpectedDate: string | null; confidence: number; categoryId: string | null; active: boolean;
  }[];
  upcoming: { total: number; items: UpcomingItem[] };
  liabilities: {
    id: string; name: string; balance: number; apr: number;
    projection: { months: number | null; totalInterest: number; payoffDate: string | null };
  }[];
  debtPlan: null | Record<string, { strategy: string; months: number | null; totalInterest: number; payoffDate: string | null }>;
}

export interface Insight {
  key: string; severity: 'info' | 'positive' | 'warning' | 'critical';
  score: number; data: Record<string, unknown>; fallback: string;
}

export interface Trends {
  period: string;
  points: { month: string; income: number; expenses: number; net: number }[];
  daily: SeriesPoint[];
  balance: SeriesPoint[];
}

export interface CategoryAnalysis {
  window: { start: string; end: string };
  total: number;
  rows: { categoryId: string | null; amount: number; share: number }[];
}

export interface ComparisonResponse {
  totals: { income: Comparison; expenses: Comparison; net: Comparison };
  categories: { categoryId: string | null; current: number; previous: number; delta: number; ratio: number | null }[];
}

export interface Forecast {
  from: string; to: string; startingBalance: number; expectedIncome: number;
  knownOutflows: number; variableSpending: number; expectedBalance: number;
  lowBalance: { date: string; balance: number };
  series: { date: string; balance: number }[];
  confidence: 'high' | 'medium' | 'low';
  assumptions: Assumption[];
}

export interface ScenarioSide {
  availableNow: number; safeToSpend: number; netWorth: number;
  emergencyMonths: number | null; monthEndBalance: number;
  goals: { goalId: string; name: string; projectedDate: string | null; monthsToTarget: number | null }[];
}

export interface ScenarioResult {
  before: ScenarioSide;
  after: ScenarioSide;
  delta: {
    availableNow: number; safeToSpend: number; netWorth: number;
    emergencyMonths: number | null; monthEndBalance: number;
    goalDelayMonths: { goalId: string; name: string; months: number | null }[];
  };
  assumptions: Assumption[];
}

export interface EmergencyFund {
  essentialMonthly: number; emergencySavings: number; coverageMonths: number | null;
  targetMonths: number; targetAmount: number; shortfall: number; monthsSampled: number;
  assumptions: Assumption[];
}

export interface AskResult {
  answer: string | null;
  usedFacts?: string[];
  unavailable?: string;
  tools: string[];
  facts: Record<string, unknown>;
  source: string;
}

export interface ReceiptAnalysis {
  receiptId: string;
  requiresReview: boolean;
  problems: string[];
  suggestion: {
    merchant: string; total: number; currency: string; date: string | null;
    categoryId: string | null; items: { name: string; amount: number }[]; confidence: number;
  };
}

export interface GoogleStatus {
  connected: boolean;
  email: string | null;
  services: { login: boolean; driveBackup: boolean; sheetsExport: boolean; gmailReceipts: boolean };
  lastBackup: { at: number; filename: string; size: number | null } | null;
}

/** One raw candidate row — from Gmail, CSV or anywhere else — before review. */
export interface RawImportRow {
  date?: string; amount?: string | number; merchant?: string; description?: string; externalId?: string;
  [key: string]: unknown;
}

export interface GmailScanResult {
  scanned: number;
  matched: number;
  rows: RawImportRow[];
}

export interface StagedImportRow {
  index: number;
  occurredAt: number;
  amount: number;
  type: 'expense' | 'income';
  merchant: string | null;
  description: string | null;
  externalId: string | null;
  suggestedCategoryId: string | null;
  duplicate: { transactionId: string; confidence: number; reasons: string[] } | null;
  errors: string[];
}

export interface ImportPreview {
  batchId: string;
  stats: { total: number; valid: number; invalid: number; duplicates: number; likelyDuplicates: number };
  rows: StagedImportRow[];
}

export interface ImportCommitRow {
  occurredAt: number; amount: number; type: 'expense' | 'income';
  merchant?: string | null; description?: string | null; categoryId?: string | null;
  externalId?: string | null; importAnyway?: boolean;
}
